import { OFFICIAL_SKI_MAP_REFERENCES } from "./mountain-map-official-sources";
import { officialMountainReviewForArea } from "./mountain-official-review";

export type PisteMapVerificationStatus =
  | "verified"
  | "limited"
  | "blocked"
  | "review-pending";

export type PisteMapVerification = Readonly<{
  areaSlug: string;
  status: PisteMapVerificationStatus;
  officialMapUrl: string | null;
  officialMapLabel: string | null;
  officialStatusUrl: string | null;
  reviewedOn: string | null;
  note: string;
  entrySlugs: readonly string[];
}>;

const REVIEW_PENDING: Omit<PisteMapVerification, "areaSlug"> = {
  status: "review-pending",
  officialMapUrl: null,
  officialMapLabel: null,
  officialStatusUrl: null,
  reviewedOn: null,
  note: "Official piste-map comparison is still pending.",
  entrySlugs: [],
};

const PISTE_MAP_VERIFICATIONS: Readonly<Record<string, PisteMapVerification>> = {
  cairngorm: {
    areaSlug: "cairngorm", status: "limited",
    officialMapUrl: "https://www.cairngormmountain.co.uk/piste-map/",
    officialMapLabel: "Cairngorm official piste map", officialStatusUrl: null,
    reviewedOn: "2026-09-18", entrySlugs: ["cairngorm"],
    note: "Most mapped pistes are shown as areas. Selected GPS routes support piste timing, but coverage remains partial and mountain-wide directions are unavailable.",
  },
  "portes-du-soleil": {
    areaSlug: "portes-du-soleil",
    status: "verified",
    officialMapUrl: "https://en.portesdusoleil.com/in-winter/openings-slopes-and-lifts/",
    officialMapLabel: "Portes du Soleil official maps",
    officialStatusUrl: "https://en.portesdusoleil.com/live/",
    reviewedOn: "2026-07-27",
    note: "The linked-domain extent and main resort sectors align with the official map.",
    entrySlugs: ["morzine", "avoriaz", "les-gets", "chatel", "champery"],
  },
  "whistler-blackcomb": {
    areaSlug: "whistler-blackcomb",
    status: "verified",
    officialMapUrl: "https://www.whistlerblackcomb.com/the-mountain/about-the-mountain/trail-maps.aspx",
    officialMapLabel: "Whistler Blackcomb official trail maps",
    officialStatusUrl: "https://www.whistlerblackcomb.com/the-mountain/mountain-conditions/terrain-and-lift-status.aspx",
    reviewedOn: "2026-07-27",
    note: "Whistler and Blackcomb extents and the main lift network align with the official map.",
    entrySlugs: ["whistler"],
  },
  kronplatz: {
    areaSlug: "kronplatz",
    status: "limited",
    officialMapUrl: "https://www.kronplatz.com/en/the-kronplatz/skilifts-slopes/ski-slopes",
    officialMapLabel: "Kronplatz official slopes and lifts",
    officialStatusUrl: "https://www.kronplatz.com/en/the-kronplatz/skilifts-slopes/ski-slopes",
    reviewedOn: "2026-07-27",
    note: "The extent aligns, but the OSM-derived lift network has fewer lifts than the official map.",
    entrySlugs: ["kronplatz"],
  },
  "les-3-vallees": {
    areaSlug: "les-3-vallees",
    status: "verified",
    officialMapUrl: "https://www.les3vallees.com/en/guide/the-world-s-largest-ski-area/ski-area-map",
    officialMapLabel: "Les 3 Vallées official ski area map",
    officialStatusUrl: "https://www.les3vallees.com/en/live/lifts-and-trails-opening",
    reviewedOn: "2026-07-27",
    note: "The corrected three-valley extent and principal lift network align with the official map.",
    entrySlugs: ["courchevel", "meribel", "val-thorens", "les-menuires"],
  },
  "park-city": {
    areaSlug: "park-city",
    status: "verified",
    officialMapUrl: "https://www.parkcitymountain.com/the-mountain/about-the-mountain/trail-map",
    officialMapLabel: "Park City Mountain official trail map",
    officialStatusUrl: "https://www.parkcitymountain.com/the-mountain/mountain-conditions/terrain-and-lift-status.aspx",
    reviewedOn: "2026-07-27",
    note: "The corrected Park City and Canyons extent and principal lift network align with the official map.",
    entrySlugs: ["park-city"],
  },
  "deer-valley": {
    areaSlug: "deer-valley",
    status: "verified",
    officialMapUrl: "https://www.deervalley.com/Explore-the-Mountain/Interactive-Grooming-Map",
    officialMapLabel: "Deer Valley official interactive map",
    officialStatusUrl: "https://www.deervalley.com/explore-the-mountain/mountain-report",
    reviewedOn: "2026-07-27",
    note: "The corrected Deer Valley extent and principal lift network align with the official map.",
    entrySlugs: ["deer-valley"],
  },
  schladming: {
    areaSlug: "schladming",
    status: "verified",
    officialMapUrl: "https://www.schladming-dachstein.at/en/activities/skiing/piste-map",
    officialMapLabel: "Schladming-Dachstein official piste map",
    officialStatusUrl: "https://www.schladming-dachstein.at/en/activities/skiing/piste-map",
    reviewedOn: "2026-07-27",
    note: "The four-mountain extent, Sonneckabfahrt connector and principal lift network align with the official map.",
    entrySlugs: ["schladming"],
  },
};

export function pisteMapVerificationForArea(
  areaSlug: string,
): PisteMapVerification {
  const sourceReview = officialMountainReviewForArea(areaSlug);
  const officialMapUrl = sourceReview
    ? sourceReview.officialMapUrl
    : OFFICIAL_SKI_MAP_REFERENCES[areaSlug] ?? null;
  return PISTE_MAP_VERIFICATIONS[areaSlug] ?? {
    areaSlug,
    ...REVIEW_PENDING,
    officialMapUrl,
    officialMapLabel: officialMapUrl ? "Official resort maps and information" : null,
  };
}

export function isPisteMapBlocked(areaSlug: string): boolean {
  return pisteMapVerificationForArea(areaSlug).status === "blocked";
}

export function isCompanionPisteMapEntrySlug(entrySlug: string): boolean {
  return Object.values(PISTE_MAP_VERIFICATIONS).some(
    (verification) =>
      verification.status === "verified" &&
      verification.entrySlugs.includes(entrySlug),
  );
}

export function verifiedPisteMapAreaSlugs(): readonly string[] {
  return Object.values(PISTE_MAP_VERIFICATIONS)
    .filter((verification) => verification.status === "verified")
    .map((verification) => verification.areaSlug);
}
